import cx from 'classnames'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'

import { SanityFilter, SanitySort } from '@data/sanity/queries/types/filter'
import { SanityProductFragment } from '@data/sanity/queries/types/product'
import { SanityPrintShop } from '@data/sanity/queries/types/shop'
import { SanityCollectionStrings } from '@data/sanity/queries/types/strings'
import {
  getFilterFromUrlParameter,
  getSortParameterValue,
  getUpdatedParameter,
  useFilterAndSort,
} from '@lib/collection'
import { clampRange, parseOptionalParameter, usePrevious } from '@lib/helpers'
import { SiteContext } from '@lib/site'
import { StringsContext } from '@lib/strings-context'
import { UrlParameter, useUrlParameters } from '@lib/url'

import ProductCard from '@blocks/shop/product-card'
import Button from '@components/buttons/button'
import ComplexPortableText from '@components/portable-text/complex'
import CollectionFilter from './filter'
import CollectionFilterChips from './filter-chips'
import CollectionSort from './sort'

interface CollectionProps {
  products: SanityProductFragment[]
  featuredProductIds: number[]
  paginationLimit: number
  title?: string
  sort?: SanitySort
  filter?: SanityFilter
  printShop?: SanityPrintShop
  collectionStrings: SanityCollectionStrings
}

/**
 * The collection component.
 */
const Collection = ({
  collectionStrings,
  products,
  featuredProductIds,
  paginationLimit,
  title,
  sort,
  filter,
  printShop,
}: CollectionProps) => {
  const { isPageTransition } = useContext(SiteContext)
  const strings = useContext(StringsContext)

  const [hasPagination, setHasPagination] = useState(
    paginationLimit > 0 && products.length > paginationLimit,
  )
  const [currentCount, setCurrentCount] = useState(
    hasPagination ? paginationLimit : products.length,
  )

  const collectionItems = useRef<(HTMLDivElement | null)[]>([])
  const filterGroups = filter?.groups ?? []

  // Manage URL parameters
  const [currentParameters, setCurrentParameters] = useUrlParameters([
    {
      name: 'page',
      value: null,
    },
    {
      name: 'sort',
      value: sort?.options?.[0]?.type ?? null,
    },
    ...filterGroups.map((filterGroup) => ({
      name: filterGroup.slug,
      value: null,
    })),
  ])
  const previousParameters = usePrevious(currentParameters)
  const activeParameters =
    isPageTransition && previousParameters
      ? previousParameters
      : currentParameters

  // Manage sorting value
  const activeSortValue = getSortParameterValue(activeParameters)

  // Manage filter values
  const activeFilters = activeParameters
    .filter((activeParameter) => {
      return !['page', 'sort'].includes(activeParameter.name)
    })
    .map(getFilterFromUrlParameter(filterGroups))
  const activeFilterValueCount = activeFilters.reduce((total, activeFilter) => {
    return total + activeFilter.values.length
  }, 0)

  // Manage sorting and pagination
  const processedProducts = useFilterAndSort(
    products,
    featuredProductIds,
    activeFilters,
    activeSortValue,
  )
  const paginatedProducts = [...processedProducts.slice(0, currentCount)]

  // Manage product pagination
  useEffect(() => {
    const pageParameter = activeParameters.find(({ name }) => name === 'page')
    const page = Number(parseOptionalParameter(pageParameter?.value) ?? 1)
    const productCount = processedProducts.length
    const pageCount = Math.ceil(productCount / paginationLimit)

    if (page <= 1 || page > pageCount) {
      return
    }

    const newCount = clampRange(paginationLimit * page, 1, productCount)

    if (newCount !== currentCount) {
      setCurrentCount(newCount)
    }
  }, [currentCount, processedProducts, activeParameters, paginationLimit])

  // Focus on the first link in the first or last product card
  useEffect(() => {
    const pageProductIndex =
      currentCount < processedProducts.length
        ? currentCount - paginationLimit
        : processedProducts.length - 1
    collectionItems.current[pageProductIndex]
      ?.querySelector<HTMLAnchorElement>('[href]')
      ?.focus({ preventScroll: true })
  }, [currentCount, paginationLimit, processedProducts.length])

  // Update pagination status
  useEffect(() => {
    setHasPagination(currentCount < processedProducts.length)
  }, [currentCount, processedProducts.length])

  // Handle filter and sort updates
  const updateParameters = useCallback(
    (newParameters: UrlParameter[]) => {
      setCurrentParameters(
        activeParameters.map((activeParameter) => {
          return getUpdatedParameter(newParameters, activeParameter)
        }),
      )
    },
    [activeParameters, setCurrentParameters],
  )

  // Handle loading more products
  const loadMore = useCallback(() => {
    const newCount = clampRange(
      currentCount + paginationLimit,
      1,
      processedProducts.length,
    )

    const newPage = Math.ceil(newCount / paginationLimit)

    setCurrentCount(newCount)
    updateParameters([
      {
        name: 'page',
        value: `${newPage > 1 ? `${newPage}` : null}`,
      },
    ])
  }, [currentCount, processedProducts, paginationLimit, updateParameters])

  const clearFilters = () => {
    updateParameters(
      activeFilters.map((activeFilter) => ({
        name: activeFilter.name,
        value: null,
      })),
    )
  }

  if (products.length === 0) {
    return null
  }

  return (
    <section>
      <div className="sticky top-[var(--headerHeight)] z-10 bg-pageBG bg-opacity-95">
        <div className="container flex justify-between">
          {filter?.isActive && (
            <CollectionFilter
              collectionStrings={collectionStrings}
              filterGroups={filterGroups}
              activeFilters={activeFilters}
              activeFilterValueCount={activeFilterValueCount}
              itemTotal={processedProducts.length}
              onChange={updateParameters}
            />
          )}

          {sort?.isActive && (
            <CollectionSort
              collectionStrings={collectionStrings}
              sortOptions={sort.options ?? []}
              activeSortValue={activeSortValue}
              onChange={updateParameters}
            />
          )}
        </div>
      </div>

      {filter?.isActive && (
        <div className="bg-blue">
          <div className="container">
            <CollectionFilterChips
              collectionStrings={collectionStrings}
              filterGroups={filterGroups}
              activeFilters={activeFilters}
              activeFilterValueCount={activeFilterValueCount}
              onClick={updateParameters}
            />
          </div>
        </div>
      )}

      <div className="container relative z-0 overflow-hidden">
        <div
          className={cx('grid gap-x-10 -m-px', {
            'grid-cols-2 md:grid-cols-4': processedProducts.length > 0,
            'grid-cols-1': processedProducts.length === 0,
          })}
        >
          {paginatedProducts.map((product, index) => {
            const key =
              product.id +
              activeParameters
                .filter((activeParam) => activeParam.name !== 'page')
                .map((activeParam) => activeParam.value)
                .filter(Boolean)
                .join('-')

            return (
              <ProductCard
                key={key}
                ref={(node) => {
                  collectionItems.current[index] = node
                }}
                product={product}
                activeFilters={activeFilters}
                hasVisuals={!!product.photos.main || !!product.photos.listing}
                showGallery={
                  product.useGallery === 'true' && !!product.photos.main
                }
                showThumbnails={
                  product.useGallery === 'false' && !!product.photos.listing
                }
                showOption={!!product.surfaceOption}
                showPrice
                isInline
                printShop={printShop}
              />
            )
          })}
        </div>

        {processedProducts.length === 0 && (
          <div className="px-8 py-16 min-h-[35vh] text-center">
            <ComplexPortableText
              content={collectionStrings.collectionFilterNoResults}
            />
            <Button
              variant="secondary"
              color="red"
              onClick={clearFilters}
              className="mt-8"
            >
              {collectionStrings.collectionClearFiltersLabel}
            </Button>
          </div>
        )}

        {hasPagination && (
          <div className="relative py-4 sm:py-8 text-center">
            <Button variant="secondary" color="black" onClick={loadMore}>
              {strings.buttonLoadMore}
              <span className="sr-only">
                {collectionStrings.collectionProducts?.replace(
                  /{title}/gi,
                  title ? `&quot;${title}&quot;` : '',
                )}
              </span>
            </Button>
          </div>
        )}

        {processedProducts.length > 0 && (
          <div className="relative py-4 sm:py-8 text-center">
            <p aria-live="polite" role="status" aria-atomic="true">
              {collectionStrings.collectionProductCount
                ?.replace(/{count}/gi, `${paginatedProducts.length}`)
                ?.replace(/{total}/gi, `${processedProducts.length}`)}
            </p>
          </div>
        )}
      </div>
    </section>
  )
}

export default Collection
